// Shared module-option normalization used by every Admin Configurations screen
// (Form Groups, Detail Groups, Row Actions, Module Action Rules, Templates,
// Role Configuration) so there is exactly one place that turns whatever shape
// the backend returns (menus collection docs, modules collection docs, or a
// bare string) into a consistent { value, label } option list.

export function moduleTitle(value) {
  return String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// The menus collection is read by two screens that normalize it DIFFERENTLY:
// the Menus tab maps it to snake_case (menu_key/menu_name — see adminCoreApi's
// normalizeMenu), while the Configurations screens hand the RAW document here.
// Real documents carry that key in any of three spellings depending on how old
// the record is and which service wrote it, so all three are accepted. Missing
// one is not a cosmetic problem: an unrecognised shape returns null and the
// module is silently DROPPED from the picker, which is how a menu could be
// listed under Menus and yet be unconfigurable under Actions.
const KEY_SPELLINGS = [
  'menuKey', 'menu_key', 'MENUKEY',
  'module', 'key', 'moduleKey', 'value', 'apiModule',
  'name', 'modulename',
  'menuName', 'menu_name', 'MENUNAME',
];

const LABEL_SPELLINGS = [
  'menuName', 'menu_name', 'MENUNAME',
  'label', 'moduleLabel', 'moduleName', 'name', 'modulename',
];

function firstSpelling(source, spellings) {
  for (const key of spellings) {
    const raw = source?.[key];
    if (raw === undefined || raw === null) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return undefined;
}

export function normalizeModuleOption(module) {
  if (typeof module === 'string') {
    return { value: module, label: moduleTitle(module) };
  }
  const value = firstSpelling(module, KEY_SPELLINGS);
  if (!value) return null;
  return {
    value,
    label: firstSpelling(module, LABEL_SPELLINGS) ?? moduleTitle(value),
  };
}

export function normalizeModuleOptions(modules) {
  const seen = new Set();
  return (Array.isArray(modules) ? modules : [])
    .map(normalizeModuleOption)
    .filter(Boolean)
    .filter((module) => {
      if (seen.has(module.value)) return false;
      seen.add(module.value);
      return true;
    });
}
